'use client'

import { useAnalytics } from '@/hooks/useAnalytics'
import { useExternalLinkTracking } from '@/hooks/useExternalLinkTracking'
import Link from 'next/link'
import { Button } from '@/components/Button'
import { GitHubIcon } from '@/components/SocialIcons'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  AppStoreIcon,
  ChangelogIcon,
  GlobeIcon,
} from '@/components/icons'
import type { ProjectLink } from '@/lib/projectsData'

function getIconComponent(iconName: string) {
  switch (iconName) {
    case 'globe':
      return GlobeIcon
    case 'github':
      return GitHubIcon
    case 'appStore':
      return AppStoreIcon
    case 'changelog':
      return ChangelogIcon
    default:
      return GlobeIcon
  }
}

export function ProjectExternalLinks({
  links,
  projectName,
}: {
  links: ProjectLink[]
  projectName: string
}) {
  const trackExternalLink = useExternalLinkTracking()

  const handleLinkClick = (link: ProjectLink) => {
    const type =
      link.icon === 'github'
        ? 'github'
        : link.icon === 'appStore'
          ? 'appstore'
          : link.icon === 'changelog'
            ? 'changelog'
            : 'website'

    trackExternalLink(link.href, `project_detail_${projectName}`, type)
  }

  return (
    <div className="mt-6 flex flex-wrap gap-4">
      {links.map((link) => {
        const IconComponent = getIconComponent(link.icon)
        return (
          <Button
            key={link.href}
            href={link.href}
            variant="secondary"
            className="flex items-center gap-2"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleLinkClick(link)}
          >
            <IconComponent className="size-4 fill-current" />
            {link.label}
          </Button>
        )
      })}
    </div>
  )
}

export function ProjectNavigation({
  prevProject,
  nextProject,
  currentProject,
  prevSlug,
  nextSlug,
}: {
  prevProject?: { name: string } | null
  nextProject?: { name: string } | null
  currentProject: string
  prevSlug?: string
  nextSlug?: string
}) {
  const { track } = useAnalytics()

  const handleNavClick = (
    direction: 'previous' | 'next',
    toProject: string,
  ) => {
    track('project_navigation', {
      from_project: currentProject,
      to_project: toProject,
      direction,
    })
  }

  return (
    <>
      <div className="flex-1">
        {prevProject && prevSlug && (
          <Link
            href={`/projects/${prevSlug}`}
            className="group flex flex-col items-start gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
            onClick={() => handleNavClick('previous', prevProject.name)}
          >
            <div className="flex items-center gap-3">
              <ArrowLeftIcon className="size-4 stroke-current" />
              <span className="text-zinc-500 dark:text-zinc-500">Previous</span>
            </div>
            <span className="ml-7">{prevProject.name}</span>
          </Link>
        )}
      </div>
      <div className="flex-1 text-right">
        {nextProject && nextSlug && (
          <Link
            href={`/projects/${nextSlug}`}
            className="group flex flex-col items-end gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
            onClick={() => handleNavClick('next', nextProject.name)}
          >
            <div className="flex items-center gap-3">
              <span className="text-zinc-500 dark:text-zinc-500">Next</span>
              <ArrowRightIcon className="size-4 stroke-current" />
            </div>
            <span className="mr-7">{nextProject.name}</span>
          </Link>
        )}
      </div>
    </>
  )
}
